import { DragEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { App, Button, Checkbox, Input, Select, Spin, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import LeadTagsFilter from 'scenes/Leads/Components/LeadTagsFilter';

import { ApiErrorData } from 'models/ApiResponse';
import { AdminCityRow, AdminRegion, LeadCard } from 'models/Lead';
import {
  AvailableLead,
  AvailableLeadsResponse,
  BusyEntry,
  OperationContractType,
  OperationTurno,
} from 'models/OperationRequest';

import { LeadService } from 'services/LeadService';
import { OperationRequirementService } from 'services/OperationRequirementService';

import {
  AssignmentSlots,
  isAssigned,
  leadToPerson,
  personName,
  typeConflict,
} from '../data/assignment';
import { PersonAvatar } from './AssignedPersonChip';

export const POOL_COUNTRY = 'CL';
const PAGE_SIZE = 50;
const PROFILE_LEVELS = [1, 2, 3, 4, 5];
const SEARCH_DEBOUNCE_MS = 350;

interface AssignmentPoolProps {
  slots: AssignmentSlots;
  busy: Record<string, BusyEntry[]>;
  type: OperationContractType;
  forced: Record<string, string>;
  draggingLeadId: string | null;
  focusTurno: OperationTurno | null;
  focusLabel: string | null;
  focusCommune: string | null;
  dayShort: (day: string) => string;
  onClearFocus: () => void;
  onAssignToFocus: (lead: LeadCard) => void;
  onDragStart: (lead: LeadCard, e: DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
  onOpen: (leadId: string) => void;
}

function AssignmentPool({
  slots,
  busy,
  type,
  forced,
  draggingLeadId,
  focusTurno,
  focusLabel,
  focusCommune,
  dayShort,
  onClearFocus,
  onAssignToFocus,
  onDragStart,
  onDragEnd,
  onOpen,
}: AssignmentPoolProps) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const i18nBase =
    'scenes.requirements_operation.components.assign_people.board.pool';

  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');
  const [regionId, setRegionId] = useState<string | undefined>();
  const [cityId, setCityId] = useState<number | undefined>();
  const [levels, setLevels] = useState<number[]>([]);
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [hideAssigned, setHideAssigned] = useState(true);
  const [regions, setRegions] = useState<AdminRegion[]>([]);
  const [cities, setCities] = useState<AdminCityRow[]>([]);
  const [leads, setLeads] = useState<AvailableLead[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(term.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);

  useEffect(() => {
    (async () => {
      const response = await LeadService.getRegions(POOL_COUNTRY);
      if (response.success) {
        setRegions((response.data as AdminRegion[]) || []);
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) || t(`${i18nBase}.errors.regions`),
          3.5
        );
      }
    })();
  }, [message, t, i18nBase]);

  useEffect(() => {
    setCities([]);
    if (!regionId) return;
    (async () => {
      const response = await LeadService.getCities(POOL_COUNTRY, regionId);
      if (response.success) {
        setCities((response.data as AdminCityRow[]) || []);
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) || t(`${i18nBase}.errors.cities`),
          3.5
        );
      }
    })();
  }, [regionId, message, t, i18nBase]);

  const fetchPage = useCallback(
    async (nextPage: number) => {
      setLoading(true);
      if (nextPage === 1) setLeads([]);
      const response = await OperationRequirementService.getAvailableLeads({
        country: POOL_COUNTRY,
        q: query || undefined,
        regionIds: regionId ? [regionId] : undefined,
        cityIds: cityId ? [cityId] : undefined,
        scoreProfile: levels,
        tagIds,
        page: nextPage,
        pageSize: PAGE_SIZE,
      });
      setLoading(false);
      if (response.success) {
        const data = response.data as AvailableLeadsResponse;
        setLeads((prev) =>
          nextPage === 1 ? data.data || [] : [...prev, ...(data.data || [])]
        );
        setTotal(data.totalRows || 0);
        setPage(nextPage);
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) || t(`${i18nBase}.errors.leads`),
          3.5
        );
      }
    },
    [query, regionId, cityId, levels, tagIds, message, t, i18nBase]
  );

  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  const visible = useMemo(() => {
    const list = hideAssigned
      ? leads.filter((lead) => !isAssigned(slots, lead.id))
      : leads;
    if (!focusCommune) return list;
    const near = focusCommune.toLowerCase();
    return [...list].sort(
      (a, b) =>
        Number((b.cityName || '').toLowerCase() === near) -
        Number((a.cityName || '').toLowerCase() === near)
    );
  }, [leads, hideAssigned, slots, focusCommune]);

  const hasFilters =
    !!term || !!regionId || !!cityId || levels.length > 0 || tagIds.length > 0;
  const clearFilters = () => {
    setTerm('');
    setRegionId(undefined);
    setCityId(undefined);
    setLevels([]);
    setTagIds([]);
  };
  const toggleLevel = (level: number) =>
    setLevels((prev) =>
      prev.includes(level)
        ? prev.filter((n) => n !== level)
        : [...prev, level].sort()
    );

  return (
    <div className="assignment-board__pool">
      <div className="assignment-board__pool-head">
        <div className="assignment-board__pool-title">
          {t(`${i18nBase}.title`)}
        </div>
        <div className="assignment-board__pool-hint">
          {t(`${i18nBase}.hint`)}
        </div>
        <div className="assignment-board__pool-filters">
          <Input
            allowClear
            value={term}
            placeholder={t(`${i18nBase}.search_placeholder`)}
            onChange={(e) => setTerm(e.target.value)}
          />
          <div className="assignment-board__pool-grid">
            <div>
              <div className="assignment-board__field-label">
                {t(`${i18nBase}.region`)}
              </div>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                value={regionId}
                placeholder={t(`${i18nBase}.all`)}
                options={regions.map((r) => ({
                  value: r.regionId,
                  label: r.regionName,
                }))}
                onChange={(value) => {
                  setRegionId(value);
                  setCityId(undefined);
                }}
              />
            </div>
            <div>
              <div className="assignment-board__field-label">
                {t(`${i18nBase}.commune`)}
              </div>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                value={cityId}
                disabled={!regionId}
                placeholder={t(`${i18nBase}.all`)}
                options={cities.map((c) => ({
                  value: c.id,
                  label: c.cityName,
                }))}
                onChange={(value) => setCityId(value)}
              />
            </div>
          </div>
          <div>
            <div className="assignment-board__field-label">
              {t(`${i18nBase}.profile`)}
            </div>
            <div
              className="assignment-board__levels"
              role="group"
              aria-label={t(`${i18nBase}.profile`)}
            >
              {PROFILE_LEVELS.map((level) => (
                <button
                  key={level}
                  type="button"
                  aria-pressed={levels.includes(level)}
                  title={t(`${i18nBase}.profile_tag`, { level })}
                  className={`assignment-board__level${
                    levels.includes(level) ? ' assignment-board__level--on' : ''
                  }`}
                  onClick={() => toggleLevel(level)}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>
          <LeadTagsFilter
            country={POOL_COUNTRY}
            active
            value={tagIds}
            onChange={setTagIds}
            labelClassName="assignment-board__field-label"
            showLeadCount={false}
          />
          <Checkbox
            checked={hideAssigned}
            onChange={(e) => setHideAssigned(e.target.checked)}
          >
            {t(`${i18nBase}.hide_assigned`)}
          </Checkbox>
          <div className="assignment-board__pool-count">
            <span>
              {t(`${i18nBase}.count`, { shown: visible.length, total })}
            </span>
            {hasFilters && (
              <Button type="link" onClick={clearFilters}>
                {t(`${i18nBase}.clear`)}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="assignment-board__pool-list">
        {focusTurno && focusLabel && (
          <div className="assignment-board__focus-banner">
            <span>{t(`${i18nBase}.focus`, { label: focusLabel })}</span>
            <button
              type="button"
              aria-label={t(`${i18nBase}.clear_focus`)}
              onClick={onClearFocus}
            >
              ✕
            </button>
          </div>
        )}

        {visible.map((lead) => {
          const person = leadToPerson(lead);
          const busyDays = busy[lead.id] || [];
          const conflict = typeConflict(busy, lead.id, type);
          const assigned = isAssigned(slots, lead.id);
          return (
            <div
              key={lead.id}
              className={`assignment-board__person${
                draggingLeadId === lead.id
                  ? ' assignment-board__person--dragging'
                  : ''
              }`}
              draggable
              onDragStart={(e) => onDragStart(lead, e)}
              onDragEnd={onDragEnd}
              onClick={() => onOpen(lead.id)}
            >
              <PersonAvatar person={person} size={30} />
              <div className="assignment-board__person-main">
                <div className="assignment-board__person-name">
                  {personName(person)}
                </div>
                <div className="assignment-board__person-meta">
                  {[lead.cityName, lead.regionName]
                    .filter(Boolean)
                    .join(' · ') || t(`${i18nBase}.no_location`)}
                </div>
                <div className="assignment-board__person-tags">
                  {lead.scoreProfile !== null && (
                    <span className="assignment-board__tag assignment-board__tag--level">
                      {t(`${i18nBase}.profile_tag`, {
                        level: lead.scoreProfile,
                      })}
                    </span>
                  )}
                  {busyDays.length > 0 && (
                    <Tooltip
                      title={busyDays
                        .map((b) => `${dayShort(b.day)}: ${b.brand}`)
                        .join(' · ')}
                    >
                      <span className="assignment-board__tag assignment-board__tag--warn">
                        {t(`${i18nBase}.busy_tag`, {
                          days: busyDays
                            .map((b) => dayShort(b.day).toUpperCase())
                            .join('/'),
                        })}
                      </span>
                    </Tooltip>
                  )}
                  {conflict && (
                    <span
                      className={`assignment-board__tag ${
                        forced[lead.id]
                          ? 'assignment-board__tag--muted'
                          : 'assignment-board__tag--warn'
                      }`}
                    >
                      {forced[lead.id]
                        ? t(`${i18nBase}.forced_tag`)
                        : t(
                            `scenes.requirements_operation.scene.contract.${conflict.type}`
                          ).toUpperCase()}
                    </span>
                  )}
                  {assigned && (
                    <span className="assignment-board__assigned-flag">
                      {t(`${i18nBase}.assigned_tag`)}
                    </span>
                  )}
                </div>
              </div>
              {focusTurno && (
                <Tooltip title={t(`${i18nBase}.assign_to_focus`)}>
                  <Button
                    size="small"
                    shape="circle"
                    icon={<PlusOutlined />}
                    aria-label={t(`${i18nBase}.assign_to_focus`)}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAssignToFocus(lead);
                    }}
                  />
                </Tooltip>
              )}
            </div>
          );
        })}

        {loading && (
          <div className="assignment-board__pool-more">
            <Spin size="small" />
          </div>
        )}
        {!loading && visible.length === 0 && (
          <div className="assignment-board__pool-empty">
            {t(`${i18nBase}.empty`)}
          </div>
        )}
        {!loading && leads.length < total && (
          <div className="assignment-board__pool-more">
            <Button size="small" onClick={() => fetchPage(page + 1)}>
              {t(`${i18nBase}.load_more`)}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AssignmentPool;
